import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { Loader2, Plus, Trash2, Zap, RefreshCw, PowerOff, Power } from "lucide-react";
import { useTranslation } from "react-i18next";

function RuleForm({ goal, incomes, onCreated }) {
  const { t } = useTranslation();
  const [matchKey, setMatchKey] = useState("");
  const [mode, setMode] = useState("percent");
  const [value, setValue] = useState("20");
  const [applyPast, setApplyPast] = useState(true);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!matchKey.trim() || !value || Number(value) <= 0) {
      return toast.error(t("goals.rules.errFillFields", { defaultValue: "Elige un ingreso y un valor válido" }));
    }
    setSaving(true);
    try {
      const r = await api.post(`/goals/${goal.id}/rules`, {
        match_key: matchKey,
        mode,
        value: Number(value),
        active: true,
      });
      const rid = r.data.id;
      if (applyPast) {
        try {
          const bf = await api.post(`/goals/rules/${rid}/backfill`);
          toast.success(t("goals.rules.backfilled", { defaultValue: "Aportadas {{n}} operaciones pasadas", n: bf.data.created }));
        } catch (e) {
          logger.error("backfill", e);
        }
      } else {
        toast.success(t("goals.rules.saved", { defaultValue: "Regla creada" }));
      }
      setMatchKey(""); setValue("20"); setMode("percent");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("goals.rules.saveErr", { defaultValue: "No se pudo crear la regla" }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-dashed border-[#EBEBEA] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-[#1C7C54]" />
        <div className="font-semibold text-sm text-[#0A110E]">{t("goals.rules.newRule", { defaultValue: "Nueva regla automática" })}</div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
          {t("goals.rules.income", { defaultValue: "Ingreso recurrente" })}
        </Label>
        {incomes.length > 0 && (
          <Select onValueChange={(v) => setMatchKey(v)}>
            <SelectTrigger data-testid="goal-rule-income-picker">
              <SelectValue placeholder={t("goals.rules.pickIncome", { defaultValue: "Selecciona un ingreso" })} />
            </SelectTrigger>
            <SelectContent>
              {incomes.map((inc) => (
                <SelectItem key={inc.key} value={inc.example_description || inc.key}>
                  {inc.example_description || inc.key} · {formatCurrency(inc.avg_amount, inc.currency)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Input
          value={matchKey}
          onChange={(e) => setMatchKey(e.target.value)}
          placeholder={t("goals.rules.matchKeyPh", { defaultValue: "o escribe una palabra clave (ej. \"nomina\", \"alquiler\")" })}
          data-testid="goal-rule-match-key"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
            {t("goals.rules.mode", { defaultValue: "Modo" })}
          </Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="goal-rule-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="percent">{t("goals.rules.percent", { defaultValue: "% del ingreso" })}</SelectItem>
              <SelectItem value="fixed">{t("goals.rules.fixed", { defaultValue: "Importe fijo" })}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
            {mode === "percent" ? "%" : formatCurrency(0, goal.currency).replace(/[\d.,]/g, "").trim() || goal.currency}
          </Label>
          <Input
            type="number"
            min="0"
            step={mode === "percent" ? "1" : "0.01"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="goal-rule-value"
          />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-[#5F7368]">
        <input
          type="checkbox"
          checked={applyPast}
          onChange={(e) => setApplyPast(e.target.checked)}
          className="w-4 h-4 accent-[#1C7C54]"
          data-testid="goal-rule-apply-past"
        />
        {t("goals.rules.applyPast", { defaultValue: "Aplicar también a los movimientos ya importados" })}
      </label>

      <Button
        onClick={submit}
        disabled={saving}
        className="bg-[#0F291E] hover:bg-[#1C7C54] w-full"
        data-testid="goal-rule-submit"
      >
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
        {t("goals.rules.create", { defaultValue: "Crear regla" })}
      </Button>
    </div>
  );
}

export default function GoalRulesDialog({ open, onOpenChange, goal, onRefresh }) {
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [contribs, setContribs] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!goal) return;
    setLoading(true);
    try {
      const [r, c, i] = await Promise.all([
        api.get(`/goals/${goal.id}/rules`),
        api.get(`/goals/${goal.id}/contributions?limit=50`),
        api.get(`/analytics/recurring-incomes`),
      ]);
      setRules(r.data || []);
      setContribs(c.data || []);
      setIncomes(i.data || []);
    } catch (e) {
      logger.error("rules load", e);
    } finally {
      setLoading(false);
    }
  }, [goal]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggle = async (rule) => {
    try {
      await api.patch(`/goals/rules/${rule.id}`, {
        match_key: rule.match_key_raw || rule.match_key,
        mode: rule.mode,
        value: rule.value,
        active: !rule.active,
      });
      load();
    } catch (e) {
      toast.error(t("goals.rules.toggleErr", { defaultValue: "No se pudo cambiar" }));
    }
  };

  const del = async (rule) => {
    if (!window.confirm(t("goals.rules.confirmDelete", { defaultValue: "¿Eliminar la regla? Las aportaciones ya creadas se mantienen." }))) return;
    try {
      await api.delete(`/goals/rules/${rule.id}`);
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const backfill = async (rule) => {
    try {
      const r = await api.post(`/goals/rules/${rule.id}/backfill`);
      toast.success(t("goals.rules.backfilled", { n: r.data.created }));
      load();
      onRefresh?.();
    } catch (e) {
      toast.error(t("goals.rules.backfillErr", { defaultValue: "No se pudo aplicar" }));
    }
  };

  const delContrib = async (c) => {
    try {
      await api.delete(`/goals/contributions/${c.id}`);
      load();
      onRefresh?.();
    } catch (e) {
      toast.error("Error");
    }
  };

  if (!goal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="goal-rules-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#1C7C54]" />
            {t("goals.rules.title", { defaultValue: "Aportaciones automáticas" })}
          </DialogTitle>
          <p className="text-sm text-[#5F7368]">
            {t("goals.rules.subtitle", {
              defaultValue: "Cuando llegue un ingreso que coincida con la palabra clave, aportaremos automáticamente a «{{name}}».",
              name: goal.name,
            })}
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-[#5F7368]" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Existing rules */}
            {rules.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
                  {t("goals.rules.activeRules", { defaultValue: "Reglas configuradas" })}
                </h3>
                {rules.map((r) => (
                  <div
                    key={r.id}
                    className={`rounded-lg border border-[#EBEBEA] px-3 py-2 flex items-center gap-3 ${r.active ? "" : "opacity-50"}`}
                    data-testid={`goal-rule-${r.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#0A110E] truncate">
                        {r.match_key_raw || r.match_key}
                      </div>
                      <div className="text-xs text-[#5F7368] tabular-nums">
                        {r.mode === "percent"
                          ? `${r.value}% ${t("goals.rules.ofIncome", { defaultValue: "del ingreso" })}`
                          : formatCurrency(r.value, goal.currency)}
                        {" · "}{r.active ? t("goals.rules.active", { defaultValue: "Activa" }) : t("goals.rules.paused", { defaultValue: "Pausada" })}
                      </div>
                    </div>
                    <button
                      onClick={() => backfill(r)}
                      title={t("goals.rules.applyPast")}
                      className="p-2 rounded-lg hover:bg-[#EBEBEA]"
                      data-testid={`goal-rule-backfill-${r.id}`}
                    >
                      <RefreshCw className="w-4 h-4 text-[#1C7C54]" />
                    </button>
                    <button
                      onClick={() => toggle(r)}
                      className="p-2 rounded-lg hover:bg-[#EBEBEA]"
                      data-testid={`goal-rule-toggle-${r.id}`}
                    >
                      {r.active ? <PowerOff className="w-4 h-4 text-[#5F7368]" /> : <Power className="w-4 h-4 text-[#1C7C54]" />}
                    </button>
                    <button
                      onClick={() => del(r)}
                      className="p-2 rounded-lg hover:bg-[#EBEBEA]"
                      data-testid={`goal-rule-delete-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-[#D95D39]" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add rule form */}
            <RuleForm goal={goal} incomes={incomes} onCreated={() => { load(); onRefresh?.(); }} />

            {/* Contributions history */}
            <div className="space-y-2">
              <h3 className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992] flex items-center justify-between">
                {t("goals.rules.history", { defaultValue: "Aportaciones recientes" })}
                {contribs.length > 0 && (
                  <span className="tabular-nums text-[#1C7C54]">
                    {formatCurrency(
                      contribs.reduce((s, c) => s + Number(c.amount || 0), 0),
                      goal.currency,
                    )} · {contribs.length}
                  </span>
                )}
              </h3>
              {contribs.length === 0 ? (
                <div className="text-sm text-[#8A9992] text-center py-6">
                  {t("goals.rules.noContribs", { defaultValue: "Aún no hay aportaciones. Crea una regla o añade una a mano." })}
                </div>
              ) : (
                <ul className="divide-y divide-[#EBEBEA] max-h-72 overflow-y-auto">
                  {contribs.map((c) => (
                    <li key={c.id} className="py-2 flex items-center gap-3 text-sm">
                      <span className={`inline-block w-2 h-2 rounded-full ${c.source === "auto" ? "bg-[#1C7C54]" : "bg-[#8A9992]"}`} />
                      <span className="flex-1 min-w-0">
                        <div className="text-[#0A110E] truncate">{c.note || t("goals.rules.manual", { defaultValue: "Aportación manual" })}</div>
                        <div className="text-xs text-[#8A9992]">{new Date(c.created_at).toLocaleString()}</div>
                      </span>
                      <span className="tabular-nums font-semibold text-[#1C7C54]">
                        +{formatCurrency(c.amount, c.currency)}
                      </span>
                      <button
                        onClick={() => delContrib(c)}
                        className="p-1 rounded hover:bg-[#EBEBEA]"
                        data-testid={`del-contrib-${c.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-[#D95D39]" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("goals.rules.close", { defaultValue: "Cerrar" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
